import { createForm3DateTimeRangePickerComponent } from "@@/shared/form/form3-date-time-range-picker";
import { createForm3ResourcePickerMultipleComponent } from "@@/shared/form/form3-resource-picker-multi";
import type { InferTypeFromSchema } from "@towni/common";
import { useMemo } from "react";
import type { ZodSchema } from "zod";
import { createForm3CheckboxComponent } from "./form3-checkbox";
import { createForm3ColorPickerComponent } from "./form3-color-picker";
import { createForm3CsvNumberInputComponent } from "./form3-csv-number-input";
import { createForm3DatePickerComponent } from "./form3-date-picker";
import { createForm3DateRangePicker } from "./form3-date-range-picker";
import { createForm3DateTimePicker } from "./form3-date-time-picker";
import { createForm3DebugComponent } from "./form3-debug";
import { createForm3ErrorMessagesComponent } from "./form3-error-messages";
import { createForm3IconNameInputComponent } from "./form3-icon-name-input";
import { createForm3ImageUploadMultipleComponent } from "./form3-image-upload-multiple";
import { createForm3ImageUploadSingleComponent } from "./form3-image-upload-single";
import { createForm3LinearGradientPickerComponent } from "./form3-linear-gradient-picker";
import { createForm3NumberInputComponent } from "./form3-number-input";
import { createForm3PhoneNumberInputComponent } from "./form3-phone-number-input";
import { createForm3PriceInputComponent } from "./form3-price-input";
import { createForm3ProductPickerMultiComponent } from "./form3-product-picker-multi";
import { createForm3ProductPickerSingleComponent } from "./form3-product-picker-single";
import { createForm3SelectComponent } from "./form3-select";
import { createForm3TextInputComponent } from "./form3-text-input";
import { createForm3TimePickerComponent } from "./form3-time-picker";
import { createForm3TranslatableInputComponent } from "./form3-translatable-input";
import { createForm3WeekdayPickerComponent } from "./form3-weekday-picker";

type Form3Components<
    FormSchema extends ZodSchema,
    FormData extends
        InferTypeFromSchema<FormSchema> = InferTypeFromSchema<FormSchema>,
> = {
    TextInput: ReturnType<
        typeof createForm3TextInputComponent<FormSchema, FormData>
    >;
    DatePicker: ReturnType<
        typeof createForm3DatePickerComponent<FormSchema, FormData>
    >;
    DateTimePicker: ReturnType<
        typeof createForm3DateTimePicker<FormSchema, FormData>
    >;
    DateRangePicker: ReturnType<
        typeof createForm3DateRangePicker<FormSchema, FormData>
    >;
    Checkbox: ReturnType<
        typeof createForm3CheckboxComponent<FormSchema, FormData>
    >;
    ColorPicker: ReturnType<
        typeof createForm3ColorPickerComponent<FormSchema, FormData>
    >;
    PriceInput: ReturnType<
        typeof createForm3PriceInputComponent<FormSchema, FormData>
    >;
    PhoneNumberInput: ReturnType<
        typeof createForm3PhoneNumberInputComponent<FormSchema, FormData>
    >;
    FormDebug: ReturnType<typeof createForm3DebugComponent<FormData>>;
    FormErrorMessages: ReturnType<typeof createForm3ErrorMessagesComponent>;
    WeekdayPicker: ReturnType<
        typeof createForm3WeekdayPickerComponent<FormSchema, FormData>
    >;
    IconNameInput: ReturnType<
        typeof createForm3IconNameInputComponent<FormSchema, FormData>
    >;
    ProductPickerSingle: ReturnType<
        typeof createForm3ProductPickerSingleComponent<FormSchema, FormData>
    >;
    ProductPickerMulti: ReturnType<
        typeof createForm3ProductPickerMultiComponent<FormSchema, FormData>
    >;
    Select: ReturnType<typeof createForm3SelectComponent<FormSchema, FormData>>;
    TranslatableInput: ReturnType<
        typeof createForm3TranslatableInputComponent<FormSchema, FormData>
    >;
    TimePicker: ReturnType<
        typeof createForm3TimePickerComponent<FormSchema, FormData>
    >;
    ImageUploadSingle: ReturnType<
        typeof createForm3ImageUploadSingleComponent<FormSchema, FormData>
    >;
    ImageUploadMultiple: ReturnType<
        typeof createForm3ImageUploadMultipleComponent<FormSchema, FormData>
    >;
    NumberInput: ReturnType<
        typeof createForm3NumberInputComponent<FormSchema, FormData>
    >;
    NumberCsvInput: ReturnType<
        typeof createForm3CsvNumberInputComponent<FormSchema, FormData>
    >;
    LinearGradientPicker: ReturnType<
        typeof createForm3LinearGradientPickerComponent<FormSchema, FormData>
    >;
    TimeRangePicker: ReturnType<
        typeof createForm3DateTimeRangePickerComponent<FormSchema, FormData>
    >;
    ResourcePickerMultiple: ReturnType<
        typeof createForm3ResourcePickerMultipleComponent<FormSchema, FormData>
    >;
};

const useForm3Components = <
    FormSchema extends ZodSchema,
    FormData extends InferTypeFromSchema<FormSchema>,
>(): Form3Components<FormSchema, FormData> => {
    return useMemo(() => {
        return {
            // AccommodationSubTypes:
            //     createFormAccommodationSubTypesComponent<State>(),
            // AccommodationTypePicker:
            //     createFormAccommodationTypePickerComponent<State>(),
            // AccommodationTypePickers:
            //     createFormAccommodationTypePickersComponent<State>(),
            // AmenityPicker: createFormAmenityPickerComponent<State>(),
            Checkbox: createForm3CheckboxComponent<FormSchema, FormData>(),
            ColorPicker: createForm3ColorPickerComponent<
                FormSchema,
                FormData
            >(),
            LinearGradientPicker: createForm3LinearGradientPickerComponent<
                FormSchema,
                FormData
            >(),
            DatePicker: createForm3DatePickerComponent<FormSchema, FormData>(),
            DateTimePicker: createForm3DateTimePicker<FormSchema, FormData>(),
            DateRangePicker: createForm3DateRangePicker<FormSchema, FormData>(),
            FormDebug: createForm3DebugComponent<FormData>(),
            FormErrorMessages: createForm3ErrorMessagesComponent(),
            TimePicker: createForm3TimePickerComponent<FormSchema, FormData>(),
            IconNameInput: createForm3IconNameInputComponent<
                FormSchema,
                FormData
            >(),
            ImageUploadSingle: createForm3ImageUploadSingleComponent<
                FormSchema,
                FormData
            >(),
            ImageUploadMultiple: createForm3ImageUploadMultipleComponent<
                FormSchema,
                FormData
            >(),
            NumberInput: createForm3NumberInputComponent<
                FormSchema,
                FormData
            >(),
            NumberCsvInput: createForm3CsvNumberInputComponent<
                FormSchema,
                FormData
            >(),
            PhoneNumberInput: createForm3PhoneNumberInputComponent<
                FormSchema,
                FormData
            >(),
            PriceInput: createForm3PriceInputComponent<FormSchema, FormData>(),
            ProductPickerSingle: createForm3ProductPickerSingleComponent<
                FormSchema,
                FormData
            >(),
            ProductPickerMulti: createForm3ProductPickerMultiComponent<
                FormSchema,
                FormData
            >(),
            // RangeDualSliderInput: createFormRangeDualSliderComponent<State>(),
            // RangeSliderInput: createFormRangeSliderComponent<State>(),
            // RangeTextInput: Form2RangeTextInput<State>,
            // ResourceMapMapper: Form2ResourceMapMapper<State>,
            // ResourceMapPicker: Form2ResourceMapPicker<State>,
            ResourcePickerMultiple: createForm3ResourcePickerMultipleComponent<
                FormSchema,
                FormData
            >(), //Form2ResourcePickerMultiple<State>,
            // ResourcePickerSingle: Form2ResourcePickerSingle<State>,
            Select: createForm3SelectComponent<FormSchema, FormData>(),
            TextInput: createForm3TextInputComponent<FormSchema, FormData>(),
            TranslatableInput: createForm3TranslatableInputComponent<
                FormSchema,
                FormData
            >(),
            WeekdayPicker: createForm3WeekdayPickerComponent<
                FormSchema,
                FormData
            >(),
            TimeRangePicker: createForm3DateTimeRangePickerComponent<
                FormSchema,
                FormData
            >(),
        };
    }, []);
};

export { useForm3Components };
export type { Form3Components };
